import { ContactCaptureData, DataDescriptorBase, DEFAULT_CAPTURE_DATA } from "@xapp/contact-capture-handler";

/**
 * Whether the email-capture toggle should show as on for the given handler config.
 * Email capture is promoted, so it defaults to on when no EMAIL descriptor exists.
 * Reads from `capture.data` (what the handler consumes) — not `capture.fields`.
 */
export function deriveEmailEnabled(value?: ContactCaptureData): boolean {
    const emailField = value?.capture?.data?.find((d) => d.type === "EMAIL");
    return emailField ? emailField.active !== false : true;
}

/**
 * Produce an updated ContactCaptureData reflecting the onboarding data-collection
 * choices, without breaking the rest of the handler config:
 *
 * - Writes the EMAIL choice into `capture.data` (the key the handler reads), NOT
 *   `capture.fields` (which it ignores).
 * - Merges into the existing descriptor set (or the handler's DEFAULT_CAPTURE_DATA
 *   when none is configured) so toggling email never drops name/phone/zip capture.
 * - Leaves the top-level `captureLead` master switch alone (defaults to true) — the
 *   email checkbox only controls the EMAIL field's `active`, not all lead capture.
 */
export function applyEmailCapture(
    value: ContactCaptureData | undefined,
    emailEnabled: boolean,
    serviceOptions: ContactCaptureData["capture"]["serviceOptions"]
): ContactCaptureData {
    const base: DataDescriptorBase[] =
        value?.capture?.data && value.capture.data.length > 0
            ? value.capture.data
            : (DEFAULT_CAPTURE_DATA as DataDescriptorBase[]);

    const hasEmail = base.some((d) => d.type === "EMAIL");
    const data: DataDescriptorBase[] = hasEmail
        ? base.map((d) => (d.type === "EMAIL" ? { ...d, active: emailEnabled } : d))
        : [
              ...base,
              {
                  type: "EMAIL",
                  questionContentKey: "EmailQuestionContent",
                  slotName: "email",
                  active: emailEnabled,
              },
          ];

    return {
        ...value,
        captureLead: value?.captureLead ?? true,
        capture: {
            ...value?.capture,
            serviceOptions,
            data,
        },
    } as ContactCaptureData;
}
